import css from './Receipt.module.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import classNames from 'classnames'

import { IReceiptData, ReceiptStatus } from 'meta/pages/receipt'
import { RouteParams, Routes } from 'meta/routes'
import { ApiEndpoint } from 'controller/endpoint'
import { Dates } from 'utils/dates'

import Head from 'components/common/Head'
import WithAppInitialized from 'components/common/WithAppInitialized'
import withTranslation, { useI18NContext } from 'components/HOC/WithTranslation'
import Img from 'components/Media/Img'
import { useIsAppInitialized } from 'hooks/useApp'
import { useBackendApiBase } from 'hooks/useEnv'

import ReceiptNotReady from './ReceiptNotReady/ReceiptNotReady'
import ReceiptSkeleton from './ReceiptSkeleton'

const Receipt: React.FC = () => {
  const { hash } = useParams<RouteParams['Receipt']>()

  const { t } = useTranslation()

  const backendApiBase = useBackendApiBase()
  const appInitialized = useIsAppInitialized()
  const { namespacesLoaded } = useI18NContext()
  const navigate = useNavigate()

  const [receiptData, setReceiptData] = useState<IReceiptData | null>(null)
  const [showNotReady, setShowNotReady] = useState<boolean>(false)

  useEffect(() => {
    if (!appInitialized || !backendApiBase || !hash) return

    axios
      .get(`${backendApiBase}${ApiEndpoint.receipt.recap(hash)}`)
      .then((response) => {
        const data = response.data.data

        if (data.receipts?.length == 0 || data.receipts[0].status !== ReceiptStatus.issued) {
          setShowNotReady(true)
        }

        setReceiptData(data)
      })
      .catch((error) => {
        if (error.response && error.response.status !== 200) {
          navigate(Routes.NotFound.generatePath(), { replace: true })
        }
      })
  }, [appInitialized, backendApiBase, hash])

  if (!receiptData && !showNotReady) {
    return <ReceiptSkeleton />
  }

  if (receiptData && showNotReady) {
    return <ReceiptNotReady codeTransaction={receiptData.transactionCode} />
  }

  return (
    <WithAppInitialized skeleton={<ReceiptSkeleton />} showChildren={namespacesLoaded}>
      <Head>
        <meta name="robots" content={'noindex, nofollow'} />
        <link href="https://fonts.googleapis.com/css?family=Nunito Sans" rel="stylesheet" />
      </Head>
      {receiptData && receiptData.receipts && receiptData.receipts[0] && (
        <div className={css.wrapper}>
          <div className={css.box}>
            <div className={css.logo}>
              <Img alt="TicketSMS Logo" src="/logo/TicketSms_logo.svg" />
            </div>
            <div className={css.transaction}>
              <div className={css.title}>
                <span>{t('receipt:salesData')}</span>
              </div>
              <div className={css.data}>
                <div className={css.left}>
                  <span>{t('receipt:transactionCode')}</span>
                  <span>{t('receipt:progressiveDoc')}</span>
                  <span>{t('receipt:emissionDate')}</span>
                </div>
                <div className={css.right}>
                  <span>{receiptData.transactionCode}</span>
                  <span>{receiptData.receipts[0]?.id}</span>
                  <span>
                    {receiptData.receipts[0].issuedAt &&
                      Dates.format(new Date(receiptData.receipts[0].issuedAt), t('dates:common:receipt'))}
                  </span>
                </div>
              </div>
            </div>
            <div className={css.quantities}>
              <div className={css.header}>
                <div className={css.left}>
                  <span className={css.qty}>{t('receipt:qty_header')}</span>
                  <span className={css.article}>{t('receipt:art_header')}</span>
                </div>
                <div className={css.right}>
                  <span className={css.iva}>{t('receipt:iva_header')}</span>
                  <span className={css.price}>{t('receipt:price_header')}</span>
                </div>
              </div>
              {receiptData.receipts[0].cart.map((elem, key) => {
                return (
                  <div className={css.content} key={key}>
                    <div className={css.left}>
                      <span className={css.qty}>{elem.quantity}</span>
                      <span className={classNames(css.article, { [css.twodigits]: elem.quantity > 9 })}>
                        {elem.item}
                      </span>
                    </div>
                    <div className={css.right}>
                      <span className={css.iva}>{`${elem.tax * 100}%`}</span>
                      <span className={css.price}>{`${elem.unitPrice.symbol} ${(
                        parseInt(elem.unitPrice.amount, 10) / 100
                      ).toLocaleString('it-IT', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}</span>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className={css.totals}>
              <div className={css.left}>
                <span className={css.total}>{t('receipt:total')}</span>
                <span className={css.iva}>{t('receipt:total_iva')}</span>
              </div>
              <div className={css.right}>
                <span className={css.price}>{`${receiptData.receipts[0].price.total.symbol} ${(
                  parseInt(receiptData.receipts[0].price.total.amount, 10) / 100
                ).toLocaleString('it-IT', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}</span>
                <span className={css.price}>{`${receiptData.receipts[0].price.tax.symbol} ${(
                  parseInt(receiptData.receipts[0].price.tax.amount, 10) / 100
                ).toLocaleString('it-IT', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}</span>
              </div>
            </div>
            <div className={css.bottom}>
              <Img alt="Live your time" src="/receipt/bottom.svg" />
            </div>
          </div>
        </div>
      )}
    </WithAppInitialized>
  )
}

const namespaces = ['receipt']

export default withTranslation(namespaces)(Receipt)
